import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import Navbar from '../components/navbar';
import './service.css';

const AllProducts = () => {
  const [products, setProducts] = useState([]);
  const [message, setMessage] = useState('');
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);

  const { service } = useParams();

  useEffect(() => {
    const fetchUserData = async () => {
      const token = window.localStorage.getItem("token");
      if (token) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/userData`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
            },
            body: JSON.stringify({ token }),
          });
          const data = await response.json();
          if (data.status === "ok") {
            setUserData(data.data);
          } else {
            setError(data.message);
          }
        } catch (error) {
          console.error("Error:", error);
          setError(error.message);
        }
      }
    };

    const fetchProducts = async () => {
      if (!service) {
        setMessage('Service category not found.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/getServiceByMain/${service}`);
        const data = response.data;
        if (data.status === 'ok') {
          setProducts(data.data);
          setFilteredProducts(data.data);
          setMessage('');
        } else {
          setMessage(data.message);
        }
      } catch (error) {
        setMessage('Unable to load services. Please try again later.');
        console.error('Products fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
    fetchProducts();
  }, [service]);

  // Filter and sort products
  useEffect(() => {
    let filtered = products.filter(product =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.vendorId?.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.vendorId?.Address?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort products
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'price':
          return (a.Pricerange || 0) - (b.Pricerange || 0);
        case 'experience':
          return (b.vendorId?.establishment || 0) - (a.vendorId?.establishment || 0);
        default:
          return 0;
      }
    });

    setFilteredProducts(filtered);
  }, [products, searchTerm, sortBy]);

  const handleEnquiry = async (e) => {
    e.preventDefault();
    if (!userData) {
      window.localStorage.setItem('redirectAfterLogin', window.location.pathname);
      window.location.href = '/login';
      return;
    }

    const form = e.target;
    const formData = new FormData(form);
    const enquiryData = {
      productname: formData.get('name'),
      product_id: formData.get('id'),
      productPrice: formData.get('price'),
      vendorId: formData.get('vendorId'),
      UserId: userData._id,
      Username: userData.fname,
      UserNumber: userData.number,
    };

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/sendEnquiryService`, enquiryData, {
        headers: { 'Content-Type': 'application/json' },
      });
      const data = response.data;
      if (data.status === 'ok') {
        setMessage('Enquiry sent successfully!');
        setShowEnquiryModal(false);
        setTimeout(() => setMessage(''), 5000);
      } else {
        setMessage(data.message);
      }
    } catch (error) {
      console.error("Error:", error);
      setMessage('Failed to send enquiry. Please try again.');
    }
  };

  const handleWhatsAppClick = (product) => {
    const message = `Hi! I'm interested in your service: ${product.name}. Please provide more details.`;
    const whatsappUrl = `https://wa.me/${product.vendorId.number}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const openEnquiryModal = (product) => {
    setSelectedService(product);
    setShowEnquiryModal(true);
  };

  const getServiceIcon = (serviceName) => {
    const iconMap = {
      'cleaning': 'fas fa-broom',
      'repair': 'fas fa-tools',
      'plumbing': 'fas fa-wrench',
      'electrical': 'fas fa-bolt',
      'painting': 'fas fa-paint-roller',
      'security': 'fas fa-shield-alt',
      'catering': 'fas fa-utensils',
      'transport': 'fas fa-truck',
      'IT': 'fas fa-laptop-code',
      'consulting': 'fas fa-handshake',
      'default': 'fas fa-cogs'
    };

    const matchedKey = Object.keys(iconMap).find(key => 
      serviceName.toLowerCase().includes(key.toLowerCase())
    );
    return iconMap[matchedKey] || iconMap.default;
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="tradeindia-services-container">
          <div className="services-loading">
            <div className="loading-spinner"></div>
            <p>Loading services...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tradeindia-services-page">
      <Navbar />
 
      {/* Success/Error Messages */}
      {message && (
        <div className="message-container">
          <div className={`alert ${message.includes('success') ? 'alert-success' : 'alert-info'}`}>
            <i className={`fas ${message.includes('success') ? 'fa-check-circle' : 'fa-info-circle'}`}></i>
            {message}
          </div>
        </div>
      )}

      {/* Services List */}
      <div className="container services-container">
        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-triangle"></i>
            <p>{error}</p>
          </div>
        )}

        {filteredProducts.length > 0 ? (
          <div className="services-grid">
            {filteredProducts.map((product, index) => (
              <div key={product._id || index} className="service-card">
                {/* Service Image */}
                <div className="service-image-container">
                  {product.image && typeof product.image === 'string' ? (
                    <img 
                      src={`${process.env.REACT_APP_API_URL}/${product.image.replace('\\', '/')}`} 
                      className="service-image" 
                      alt={product.name}
                      loading="lazy"
                    />
                  ) : (
                    <div className="service-icon-placeholder">
                      <i className={getServiceIcon(product.name)}></i>
                    </div>
                  )}
                  
                  {/* Verified Badge */}
                  <div className="verified-badge">
                    <i className="fas fa-check-circle"></i>
                    <span>Verified</span>
                  </div>
                </div>

                {/* Service Content */}
                <div className="service-content">
                  {/* Service Header */}
                  <div className="service-header">
                    <h3 className="service-title">{product.name}</h3>
                    <div className="service-rating">
                      <div className="stars">
                        <i className="fas fa-star filled"></i>
                        <i className="fas fa-star filled"></i>
                        <i className="fas fa-star filled"></i>
                        <i className="fas fa-star filled"></i>
                        <i className="fas fa-star"></i>
                      </div>
                      <span className="rating-text">4.2 (25 reviews)</span>
                    </div>
                  </div>

                  {/* Company Info */}
                  <div className="company-info">
                    <div className="company-item">
                      <i className="fas fa-building"></i>
                      <span>{product.vendorId?.businessName || 'Business Name'}</span>
                    </div>
                    
                    <div className="company-item">
                      <i className="fas fa-map-marker-alt"></i>
                      <span>{product.vendorId?.Address || 'Location not specified'}</span>
                    </div>
                    
                    <div className="company-item">
                      <i className="fas fa-clock"></i>
                      <span>{product.vendorId?.establishment || 0} Years in Business</span>
                    </div>
                  </div>

                  {/* Price Range */}
                  {product.Pricerange && (
                    <div className="price-section">
                      <div className="price-label">Starting from</div>
                      <div className="price-value">₹{product.Pricerange?.toLocaleString('en-IN')}</div>
                    </div>
                  )}

                  {/* Service Features */}
                  <div className="service-features">
                    <div className="feature-item">
                      <i className="fas fa-shield-alt"></i>
                      <span>Verified Provider</span>
                    </div>
                    <div className="feature-item">
                      <i className="fas fa-clock"></i>
                      <span>Quick Response</span>
                    </div>
                    <div className="feature-item">
                      <i className="fas fa-medal"></i>
                      <span>Quality Service</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="service-actions">
                    <a 
                      href={`tel:${product.vendorId?.OfficeContact || product.vendorId?.number}`}
                      className="action-btn call-btn"
                    >
                      <i className="fas fa-phone"></i>
                      <span></span>
                      <div className="phone-number">
                        {product.vendorId?.OfficeContact || product.vendorId?.number}
                      </div>
                    </a>
                    
                    <button 
                      className="action-btn whatsapp-btn"
                      onClick={() => handleWhatsAppClick(product)}
                    >
                      <i className="fab fa-whatsapp"></i>
                      <span>WhatsApp</span>
                    </button>
                    
                    <button 
                      className="action-btn enquiry-btn"
                      onClick={() => openEnquiryModal(product)}
                    >
                      <i className="fas fa-envelope"></i>
                      <span>Send Enquiry</span>
                    </button>
                  </div>

                  {/* Quick Contact Info */}
                  <div className="quick-contact">
                    <div className="contact-item">
                      <i className="fas fa-user-circle"></i>
                      <span>{product.vendorId?.fname || 'Contact Person'}</span>
                    </div>
                    {product.vendorId?.email && (
                      <div className="contact-item">
                        <i className="fas fa-envelope"></i>
                        <span>{product.vendorId.email}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Service Badge */}
                <div className="service-badge">
                  <i className="fas fa-star"></i>
                  <span>Premium</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-services">
            <div className="no-services-content">
              <i className="fas fa-search"></i>
              <h3>No Services Found</h3>
              <p>
                {searchTerm 
                  ? `No services found matching "${searchTerm}"`
                  : 'No services available in this category.'
                }
              </p>
              {searchTerm && (
                <button 
                  className="clear-search-btn"
                  onClick={() => setSearchTerm('')}
                >
                  <i className="fas fa-times"></i>
                  Clear Search
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Enquiry Modal */}
      {showEnquiryModal && selectedService && (
        <div className="modal-overlay" onClick={() => setShowEnquiryModal(false)}>
          <div className="enquiry-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Send Service Enquiry</h3>
              <button 
                className="close-modal"
                onClick={() => setShowEnquiryModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="modal-body">
              <div className="service-summary">
                {selectedService.image && typeof selectedService.image === 'string' ? (
                  <img 
                    src={`${process.env.REACT_APP_API_URL}/${selectedService.image.replace('\\', '/')}`}
                    alt={selectedService.name}
                  />
                ) : (
                  <div className="placeholder-image">
                    <i className={getServiceIcon(selectedService.name)}></i>
                  </div>
                )}
                <div className="service-details">
                  <h4>{selectedService.name}</h4>
                  <p>{selectedService.vendorId?.businessName}</p>
                  {selectedService.Pricerange && (
                    <span className="price">₹{selectedService.Pricerange?.toLocaleString('en-IN')}</span>
                  )}
                </div>
              </div>
              
              <form onSubmit={handleEnquiry}>
                <input type="hidden" name="name" value={selectedService.name} />
                <input type="hidden" name="id" value={selectedService._id} />
                <input type="hidden" name="price" value={selectedService.Pricerange} />
                <input type="hidden" name="vendorId" value={selectedService.vendorId?._id} />
                
                <button type="submit" className="submit-enquiry">
                  <i className="fas fa-paper-plane"></i>
                  Send Enquiry
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllProducts;